import { CalculatorOutlined, DownloadOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Input, Row, Select, Space, Statistic, Table, Tabs, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";

import { formatTime } from "../../api/format";
import { useNames } from "../../api/names";
import { downloadText, toCsv, useScoreReport } from "../../api/scores";
import type { Group, ScoreReportCandidate } from "../../api/types";
import { CandidateDrawer } from "../candidates/CandidateDrawer";

export function ScoresPage() {
  const { catalog, groupName, regionName, siteName } = useNames();
  const report = useScoreReport();
  const [group, setGroup] = useState<Group>();
  const [regionId, setRegionId] = useState<number>();
  const [siteId, setSiteId] = useState<number>();
  const [keyword, setKeyword] = useState("");
  const [openUsername, setOpenUsername] = useState<string>();
  const groups = catalog.data?.groups ?? [];
  const regions = catalog.data?.regions ?? [];
  const sites = (catalog.data?.sites ?? []).filter((site) => regionId === undefined || site.regionId === regionId);

  useEffect(() => {
    if (group === undefined && groups.length > 0) setGroup(groups[0].id);
  }, [group, groups]);

  const data = report.data;
  const rows = useMemo(() => {
    const text = keyword.trim().toLowerCase();
    return (data?.candidates ?? []).filter((row) => !text || row.username.toLowerCase().includes(text) || row.name.toLowerCase().includes(text));
  }, [data, keyword]);

  const compute = () => {
    if (group) report.mutate({ group, regionId, siteId });
  };

  const download = () => {
    if (!data || !group) return;
    const csv = toCsv(
      ["准考证号", "姓名", "组别", "考点区域", "考点", "考点编号", "是否参加", "已作答题数", "总分", "满分", "组内排名", "考点内排名", "账号状态"],
      data.candidates.map((row) => [
        row.username, row.name, groupName(group), regionName(row.regionId), siteName(row.siteId), row.siteId,
        row.answered > 0 ? "是" : "否", row.answered, row.total, data.exam.totalScore, row.groupRank, row.siteRank,
        row.enabled ? "启用" : "已关闭"
      ])
    );
    const stamp = data.computedAt.replace(/[-:]/g, "").slice(0, 15);
    downloadText(`scores-${group}-${stamp}.csv`, csv);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Space wrap>
        <Select<Group> style={{ width: 140 }} value={group} onChange={setGroup}
          options={groups.map((item) => ({ value: item.id, label: item.name }))} />
        <Select<number> allowClear showSearch optionFilterProp="label" placeholder="全部考点区域" style={{ width: 160 }} value={regionId}
          onChange={(value) => { setRegionId(value); setSiteId(undefined); }}
          options={regions.map((item) => ({ value: item.id, label: item.name }))} />
        <Select<number> allowClear showSearch optionFilterProp="label" placeholder="全部考点" style={{ width: 220 }} value={siteId}
          onChange={setSiteId}
          options={sites.map((item) => ({ value: item.id, label: `${item.name}（${item.id}）` }))} />
        <Button type="primary" icon={<CalculatorOutlined />} loading={report.isPending} disabled={!group} onClick={compute}>
          计算成绩
        </Button>
        <Button icon={<DownloadOutlined />} disabled={!data} onClick={download}>下载成绩 CSV</Button>
        {data && <Typography.Text type="secondary">{data.exam.title}，满分 {data.exam.totalScore}，计算时间 {formatTime(data.computedAt)}</Typography.Text>}
      </Space>
      {report.error && <Alert type="error" showIcon message={report.error.message} />}
      {!data && !report.isPending && <Alert type="info" showIcon message="选择组别后点击“计算成绩”。成绩按当前答案即时计算。" />}
      {data && (
        <>
          <Row gutter={[16, 16]}>
            {[
              ["考生数", data.summary.candidates],
              ["参加人数", data.summary.participants],
              ["平均分", data.summary.average ?? "—"],
              ["最高分", data.summary.max ?? "—"],
              ["最低分", data.summary.min ?? "—"]
            ].map(([title, value]) => (
              <Col key={title} xs={12} md={8} lg={4}>
                <Card><Statistic title={title} value={value} /></Card>
              </Col>
            ))}
          </Row>
          <Tabs
            items={[
              {
                key: "candidates",
                label: `考生成绩（${data.candidates.length}）`,
                children: (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Input.Search allowClear placeholder="准考证号或姓名" style={{ width: 240 }} onSearch={setKeyword} onChange={(event) => { if (!event.target.value) setKeyword(""); }} />
                    <Table<ScoreReportCandidate>
                      rowKey="username"
                      size="middle"
                      dataSource={rows}
                      pagination={{ pageSize: 50, showSizeChanger: false, showTotal: (total) => `共 ${total} 人` }}
                      columns={[
                        { title: "准考证号", dataIndex: "username", sorter: (a, b) => a.username.localeCompare(b.username), defaultSortOrder: "ascend", render: (value: string) => <a onClick={() => setOpenUsername(value)}>{value}</a> },
                        { title: "姓名", dataIndex: "name" },
                        { title: "考点区域", dataIndex: "regionId", render: regionName },
                        { title: "考点", dataIndex: "siteId", render: siteName },
                        { title: "组内排名", dataIndex: "groupRank", sorter: (a, b) => a.groupRank - b.groupRank, width: 100 },
                        { title: "考点内排名", dataIndex: "siteRank", sorter: (a, b) => a.siteRank - b.siteRank, width: 110 },
                        { title: "已作答", dataIndex: "answered", sorter: (a, b) => a.answered - b.answered, render: (value: number) => `${value} / ${data.exam.questionCount}` },
                        { title: "总分", dataIndex: "total", sorter: (a, b) => a.total - b.total, align: "right", render: (value: number) => <strong>{value}</strong> },
                        { title: "参加", render: (_, row) => row.answered > 0 ? <Tag color="green">是</Tag> : <Tag>否</Tag> },
                        { title: "账号", render: (_, row) => row.enabled ? <Tag color="green">启用</Tag> : <Tag color="red">已关闭</Tag> }
                      ]}
                    />
                  </Space>
                )
              },
              {
                key: "distribution",
                label: "分数段分布",
                children: (
                  <Table
                    rowKey="from"
                    size="middle"
                    pagination={false}
                    dataSource={data.distribution}
                    columns={[
                      { title: "分数段", render: (_, row) => `${row.from} – ${row.to}` },
                      { title: "人数", dataIndex: "count", align: "right" },
                      { title: "占参加人数", align: "right", render: (_, row) => data.summary.participants ? `${Math.round((row.count / data.summary.participants) * 1000) / 10}%` : "—" }
                    ]}
                  />
                )
              },
              {
                key: "sites",
                label: `各考点（${data.sites.length}）`,
                children: (
                  <Table
                    rowKey="siteId"
                    size="middle"
                    pagination={false}
                    dataSource={data.sites}
                    columns={[
                      { title: "区域", dataIndex: "regionId", render: regionName },
                      { title: "考点", dataIndex: "siteId", render: (id: number) => `${siteName(id)}（${id}）` },
                      { title: "考生数", dataIndex: "total", align: "right" },
                      { title: "参加人数", dataIndex: "participants", align: "right" },
                      { title: "平均分", dataIndex: "average", align: "right", render: (value: number | null) => value ?? "—" },
                      { title: "最高分", dataIndex: "max", align: "right", render: (value: number | null) => value ?? "—" }
                    ]}
                  />
                )
              },
              {
                key: "questions",
                label: `各题正确率（${data.questions.length}）`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={data.questions}
                    columns={[
                      { title: "题号", dataIndex: "id" },
                      { title: "分值", dataIndex: "score", align: "right" },
                      { title: "答对人数", dataIndex: "correct", align: "right" },
                      { title: "正确率", dataIndex: "rate", align: "right", sorter: (a, b) => (a.rate ?? 0) - (b.rate ?? 0), render: (value: number | null) => value === null ? "—" : `${Math.round(value * 1000) / 10}%` }
                    ]}
                  />
                )
              }
            ]}
          />
        </>
      )}
      <CandidateDrawer username={openUsername} onClose={() => setOpenUsername(undefined)} />
    </Space>
  );
}
